I<template>
  <div class="delete-worker">
    <div class="header-section">
      <h2>🗑️ {{ $t('deleteWorker.title') }}</h2>
      <button @click="closeDeleteWorker" class="close-btn">✕</button>
    </div>

    <div class="shift-info">
      <h3>📅 {{ formatFullDate(shift.start_time) }}</h3>
      <p class="shift-time">{{ $t('deleteWorker.shiftTime') }}: {{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }}</p>
    </div>

    <div class="worker-selection-section">
      <h4>{{ $t('deleteWorker.selectWorker') }}:</h4>
      <div v-if="shift.assignments && shift.assignments.length > 0" class="workers-list">
        <div 
          v-for="assignment in shift.assignments" 
          :key="assignment.id"
          class="worker-item"
          :class="{ selected: selectedAssignmentIds.includes(assignment.id) }"
          @click="toggleWorkerSelection(assignment.id)"
        >
          <div class="worker-info">
            <div class="worker-name">{{ assignment.worker.first_name }} {{ assignment.worker.last_name }}</div>
            <div class="worker-role">{{ assignment.worker.role }}</div>
            <div v-if="assignment.sick_leave_type || assignment.unauthorized_absence" class="worker-status">
              <span v-if="assignment.sick_leave_type" class="status-badge sick">
                {{ assignment.sick_leave_type === 'full' ? 'Sick Leave' : 'Partial Sick' }}
              </span>
              <span v-if="assignment.unauthorized_absence" class="status-badge absence">
                Unauthorized Absence
              </span>
            </div>
          </div>
          <div class="selection-indicator">
            <span v-if="selectedAssignmentIds.includes(assignment.id)" class="checkmark">✓</span>
          </div>
        </div>
      </div>
      
      <div v-else class="no-workers">
        <p>{{ $t('deleteWorker.noWorkersAssigned') }}</p>
      </div>
    </div>

    <div v-if="message" class="message" :class="messageType">
      {{ message }}
    </div>

    <div class="form-actions">
      <button 
        @click="removeSelectedWorkers" 
        class="remove-btn" 
        :disabled="selectedAssignmentIds.length === 0 || removing"
      >
        {{ removing ? $t('common.loading') : `${$t('deleteWorker.removeWorker')} (${selectedAssignmentIds.length})` }}
      </button>
      <button @click="closeDeleteWorker" class="cancel-btn">{{ $t('common.cancel') }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useShiftStore } from '../stores/shift.js'
import { shiftApi } from '../services/shiftApi.js'

const props = defineProps({
  shift: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['worker-deleted', 'close'])

// Reactive data
const shiftStore = useShiftStore()
const selectedAssignmentIds = ref([])
const removing = ref(false)
const message = ref('')
const messageType = ref('success')

// Methods
function toggleWorkerSelection(assignmentId) {
  const index = selectedAssignmentIds.value.indexOf(assignmentId)
  if (index > -1) {
    selectedAssignmentIds.value.splice(index, 1)
  } else {
    selectedAssignmentIds.value.push(assignmentId)
  }
}

async function removeSelectedWorkers() {
  if (selectedAssignmentIds.value.length === 0) return
  
  try {
    removing.value = true
    
    // Remove each selected assignment
    const removePromises = selectedAssignmentIds.value.map(assignmentId => 
      shiftApi.removeWorkerFromShift(assignmentId)
    )
    
    await Promise.all(removePromises)
    
    showMessage(`Removed ${selectedAssignmentIds.value.length} worker(s) from shift`, 'success')
    
    // Notify store to trigger reactivity in other components
    shiftStore.notifyShiftsChanged()
    
    // Emit success event
    emit('worker-deleted')
    
  } catch (error) {
    console.error('Error removing workers:', error)
    showMessage('Failed to remove workers: ' + error.message, 'error')
  } finally {
    removing.value = false
  }
}

function closeDeleteWorker() {
  emit('close')
}

function showMessage(text, type = 'success') {
  message.value = text
  messageType.value = type
  setTimeout(() => {
    message.value = ''
  }, 3000)
}

// Helper functions
function formatFullDate(dateTimeString) {
  const date = new Date(dateTimeString)
  return date.toLocaleDateString('sv-SE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

function formatTime(dateTimeString) {
  const date = new Date(dateTimeString)
  return date.toLocaleTimeString('sv-SE', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false
  })
}
</script>

<style scoped>
.delete-worker {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
}

.header-section h2 {
  margin: 0;
  color: #1e293b;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
  padding: 0.5rem;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.shift-info {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 8px;
}

.shift-info h3 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
}

.shift-time {
  font-size: 1.2rem;
  font-weight: 600;
  color: #667eea;
  margin: 0;
}

.worker-selection-section h4 {
  margin: 0 0 1rem 0;
  color: #1e293b;
}

.workers-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
}

.worker-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.worker-item:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.worker-item.selected {
  border-color: #ef4444;
  background: #fef2f2;
}

.worker-info {
  flex: 1;
}

.worker-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.worker-role {
  color: #64748b;
  font-size: 0.875rem;
  text-transform: capitalize;
  margin-bottom: 0.25rem;
}

.worker-status {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
}

.status-badge.sick {
  background: #fff3e0;
  color: #f57c00;
  border: 1px solid #ffb74d;
}

.status-badge.absence {
  background: #ffebee;
  color: #d32f2f;
  border: 1px solid #ef5350;
}

.selection-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.worker-item.selected .selection-indicator {
  background: #ef4444;
  border-color: #ef4444;
}

.checkmark {
  color: white;
  font-weight: bold;
}

.no-workers {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}

.message {
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-weight: 500;
}

.message.success {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.message.error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.remove-btn {
  padding: 0.75rem 1.5rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.remove-btn:hover:not(:disabled) {
  background: #dc2626;
}

.remove-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #4b5563;
}
</style>